import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  Button,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Warning as WarningIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  DeveloperBoard as CpuIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Link as LinkIcon,
  AutoAwesome as AutoAwesomeIcon,
  InfoOutlined as InfoIcon,
  History as HistoryIcon
} from '@mui/icons-material';

import { useAppData } from '../contexts/AppDataContext';
import PageHeader from '../components/common/PageHeader';
import LoadingOverlay from '../components/common/LoadingOverlay';

const AlertsPage = () => {
  const theme = useTheme();
  const { 
    alerts, 
    fetchAlerts, 
    loading, 
    jiraConfig, 
    fetchJiraConfig, 
    createJiraTicket,
    fetchAlertTickets,
    alertTickets 
  } = useAppData();
  const navigate = useNavigate();
  
  // Local state
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketResult, setTicketResult] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [showJiraInfo, setShowJiraInfo] = useState(false);
  const [alertsWithTickets, setAlertsWithTickets] = useState({});
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Fetch alerts, Jira config, and ticket history on mount
  useEffect(() => {
    console.log("AlertsPage: Fetching alerts and Jira config");
    fetchAlerts();
    
    // Explicitly fetch and log Jira config for debugging
    fetchJiraConfig().then(config => {
      console.log("Jira Config received:", config);
    }).catch(error => {
      console.error("Error fetching Jira config:", error);
    });
    
    fetchAlertTickets();
    
    // Set up auto-refresh
    const interval = setInterval(() => {
      fetchAlerts();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [fetchAlerts, fetchJiraConfig, fetchAlertTickets]);
  
  // Process alert tickets to know which alerts already have tickets
  useEffect(() => {
    if (alertTickets && alertTickets.length > 0 && alerts && alerts.length > 0) {
      // Create a map of hostname+type to ticket info
      const ticketMap = {};
      
      alertTickets.forEach(ticket => {
        const key = `${ticket.hostname}-${ticket.type}`;
        // Convert assignee object to string if needed
        let assigneeDisplay = 'Unknown';
        if (ticket.assignee) {
          assigneeDisplay = typeof ticket.assignee === 'object' 
            ? (ticket.assignee.displayName || ticket.assignee.accountId || 'Unknown')
            : ticket.assignee;
        }
        
        ticketMap[key] = {
          ticket_key: ticket.ticket_key,
          ticket_url: ticket.ticket_url,
          assignee: assigneeDisplay
        };
      });
      
      setAlertsWithTickets(ticketMap);
    }
  }, [alertTickets, alerts]);
  
  // Function to handle creating a ticket
  const handleCreateTicket = (alert) => {
    setSelectedAlert(alert);
    setShowTicketDialog(true);
  };

  // Function to submit ticket creation
  const handleSubmitTicket = async () => {
    if (!selectedAlert) return;
    
    try {
      const result = await createJiraTicket(selectedAlert);
      setTicketResult(result);
      
      if (result.status) {
        setSnackbarSeverity('success');
        setSnackbarMessage(`Ticket ${result.ticket_key} created successfully!`);
        
        // Convert assignee to string if it's an object
        let assigneeDisplay = 'Unknown';
        if (result.assignee) {
          assigneeDisplay = typeof result.assignee === 'object' 
            ? (result.assignee.displayName || result.assignee.accountId || 'Unknown')
            : result.assignee;
        }
        
        // Update the alertsWithTickets state
        setAlertsWithTickets(prev => ({
          ...prev,
          [`${selectedAlert.hostname}-${selectedAlert.type}`]: {
            ticket_key: result.ticket_key,
            ticket_url: result.ticket_url,
            assignee: assigneeDisplay
          }
        }));
        
        // Refresh ticket history
        fetchAlertTickets();
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(result.message || 'Failed to create ticket');
      }
      
      setShowSnackbar(true);
      setShowTicketDialog(false);
    } catch (error) {
      console.error('Error creating ticket:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('An error occurred while creating the ticket');
      setShowSnackbar(true);
      setShowTicketDialog(false);
    }
  };
  
  // Function to create all tickets at once
  const handleCreateAllTickets = async () => {
    try {
      setSnackbarSeverity('info');
      setSnackbarMessage('Creating tickets for all alerts...');
      setShowSnackbar(true);
      
      const result = await fetchAlerts(true);
      
      if (result && result.created_tickets && result.created_tickets.length > 0) {
        setSnackbarSeverity('success');
        setSnackbarMessage(`Created ${result.created_tickets.length} tickets successfully!`);
        
        // Update tickets map
        const newTicketsMap = { ...alertsWithTickets };
        result.created_tickets.forEach(ticket => {
          const key = `${ticket.hostname}-${ticket.alert_type}`;
          
          // Convert assignee to string if it's an object
          let assigneeDisplay = 'Unknown';
          if (ticket.assignee) {
            assigneeDisplay = typeof ticket.assignee === 'object' 
              ? (ticket.assignee.displayName || ticket.assignee.accountId || 'Unknown')
              : ticket.assignee;
          }
          
          newTicketsMap[key] = {
            ticket_key: ticket.ticket_key,
            ticket_url: ticket.ticket_url,
            assignee: assigneeDisplay
          };
        });
        
        setAlertsWithTickets(newTicketsMap);
        
        // Refresh ticket history
        fetchAlertTickets();
      } else {
        setSnackbarSeverity('warning');
        setSnackbarMessage('No new tickets were created');
      }
      
      setShowSnackbar(true);
    } catch (error) {
      console.error('Error creating all tickets:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to create tickets');
      setShowSnackbar(true);
    }
  };
  
  // Helper function to get icon for alert type
  const getAlertIcon = (type) => {
    switch (type) {
      case 'cpu':
        return <CpuIcon />;
      case 'memory':
        return <MemoryIcon />;
      case 'disk':
        return <StorageIcon />;
      default:
        return <WarningIcon />;
    }
  };

  // Helper function to get color for alert type
  const getAlertColor = (type) => {
    switch (type) {
      case 'cpu':
        return theme.palette.error.main;
      case 'memory':
        return theme.palette.warning.main;
      case 'disk':
        return theme.palette.info.main;
      default:
        return theme.palette.error.main;
    }
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / 60000);
      
      // Just now (less than a minute)
      if (diffMinutes < 1) {
          return 'Just now';
      }
      
      // Minutes ago (less than an hour)
      if (diffMinutes < 60) {
          return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
      }
      
      // Hours ago (less than a day)
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) {
          return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      }
      
      // Days ago (less than a week)
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) {
          return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      }
      
      // Actual date time for older alerts
      return date.toLocaleString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Unknown time';
    }
  };
  
  // Check if an alert already has a ticket
  const hasTicket = (alert) => {
    if (!alert) return false;
    return alertsWithTickets[`${alert.hostname}-${alert.type}`] !== undefined;
  };
  
  // Get ticket info for an alert
  const getTicketInfo = (alert) => {
    if (!alert) return null;
    return alertsWithTickets[`${alert.hostname}-${alert.type}`];
  };

  const isJiraEnabled = () => {
    // Check if jiraConfig.jira_enabled is true (this is the property sent from backend)
    return Boolean(jiraConfig?.jira_enabled);
  };

  return (
    <Box>
      <PageHeader
        title="System Alerts"
        subtitle="Monitoring resource usage alerts"
        icon={WarningIcon}
        actions={
          <Stack direction="row" spacing={1}>
            {/* Always show Jira buttons for testing */}
            <React.Fragment>
              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleCreateAllTickets}
                startIcon={<AutoAwesomeIcon />}
                sx={{ 
                  borderRadius: '10px', 
                  textTransform: 'none',
                  fontWeight: 'medium',
                }}
              >
                Create All Tickets
              </Button>
              <Tooltip title="View Ticket History">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowHistoryDialog(true)}
                  startIcon={<HistoryIcon />}
                  sx={{ 
                    borderRadius: '10px', 
                    textTransform: 'none',
                    fontWeight: 'medium',
                  }}
                >
                  History
                </Button>
              </Tooltip>
              <IconButton 
                color="primary" 
                onClick={() => setShowJiraInfo(true)}
                sx={{ ml: 1 }}
              >
                <InfoIcon />
              </IconButton>
            </React.Fragment>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/')}
              startIcon={<ArrowBackIcon />}
              sx={{ 
                borderRadius: '10px', 
                textTransform: 'none',
                fontWeight: 'medium',
              }}
            >
              Back to Dashboard
            </Button>
          </Stack>
        }
      />

      <LoadingOverlay loading={loading.alerts} message="Loading alerts...">
        <Box mt={2}>
          {alerts && alerts.length > 0 ? (
            <Grid container spacing={2}>
              {alerts.map((alert, index) => (
                <Grid item xs={12} key={`alert-${index}`}>
                  <Card sx={{ 
                    borderRadius: '12px',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: `0 8px 16px rgba(0, 0, 0, 0.1)`,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(getAlertColor(alert.type), 0.2)}`,
                  }}>
                    <Box 
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box 
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          backgroundColor: alpha(getAlertColor(alert.type), 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: getAlertColor(alert.type),
                          mr: 2,
                        }}
                      >
                        {getAlertIcon(alert.type)}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="h6" fontWeight="medium">
                            {alert.type.toUpperCase()} Alert
                          </Typography>
                          <Box>
                            <Chip 
                              label={formatDate(alert.timestamp)}
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                                color: theme.palette.text.secondary,
                                mr: 1
                              }}
                            />
                            {/* Always show ticket options for testing */}
                            {hasTicket(alert) ? (
                              <Tooltip title={`View Jira ticket ${getTicketInfo(alert)?.ticket_key}`}>
                                <Chip
                                  label={getTicketInfo(alert)?.ticket_key}
                                  size="small"
                                  color="success"
                                  icon={<AssignmentIcon fontSize="small" />}
                                  onClick={() => window.open(getTicketInfo(alert)?.ticket_url, '_blank')}
                                  sx={{ 
                                    cursor: 'pointer',
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                color="secondary"
                                startIcon={<AssignmentIcon />}
                                onClick={() => handleCreateTicket(alert)}
                                sx={{ 
                                  borderRadius: '14px', 
                                  textTransform: 'none',
                                  fontWeight: 'medium',
                                  height: '24px'
                                }}
                              >
                                Create Ticket
                              </Button>
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {alert.message}
                        </Typography>
                        <Box mt={1}>
                          <Chip 
                            label={alert.hostname}
                            size="small"
                            sx={{ 
                              mr: 1,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Chip 
                            label={`${alert.value?.toFixed(1) || 0}% (Threshold: ${alert.threshold}%)`}
                            size="small"
                            sx={{ 
                              backgroundColor: alpha(getAlertColor(alert.type), 0.1),
                              color: getAlertColor(alert.type),
                            }}
                          />
                          <Chip 
                            label={alert.region || 'Unknown'}
                            size="small"
                            sx={{ 
                              ml: 1,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: 300,
                backgroundColor: theme.palette.background.paper,
                borderRadius: '12px',
                boxShadow: `0 8px 16px rgba(0, 0, 0, 0.1)`,
              }}
            >
              <WarningIcon 
                sx={{ 
                  fontSize: 60, 
                  color: alpha(theme.palette.text.secondary, 0.2),
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary">
                No active alerts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All systems are operating normally
              </Typography>
            </Box>
          )}
        </Box>
      </LoadingOverlay>
      
      {/* Create Ticket Dialog */}
      <Dialog 
        open={showTicketDialog} 
        onClose={() => setShowTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create Jira Ticket
          <IconButton
            onClick={() => setShowTicketDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedAlert.type.toUpperCase()} Alert on {selectedAlert.hostname}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                {selectedAlert.message}
              </Typography>
              
              <Box mt={2}>
                <Chip 
                  label={`Region: ${selectedAlert.region}`}
                  size="medium"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip 
                  label={`Value: ${selectedAlert.value?.toFixed(1) || 0}%`}
                  size="medium"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip 
                  label={`Threshold: ${selectedAlert.threshold}%`}
                  size="medium"
                  sx={{ mr: 1, mb: 1 }}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" mt={2}>
                This will create a Jira ticket in project {jiraConfig?.project_key || "Default"}.
                {selectedAlert.region && jiraConfig?.regions && Array.isArray(jiraConfig.regions) && jiraConfig.regions.includes(selectedAlert.region) && (
                  <span>
                    {" "}The ticket will be assigned to{" "}
                    <Box component="span" fontWeight="bold">
                      {(() => {
                        const assignee = jiraConfig?.region_assignees?.[selectedAlert.region] || 
                                         jiraConfig?.region_assignees?.["default"];
                        if (!assignee) return "the default assignee";
                        return typeof assignee === 'object' 
                          ? (assignee.displayName || assignee.accountId || "the default assignee") 
                          : assignee;
                      })()}
                    </Box>
                    {" "}for the {selectedAlert.region} region.
                    {(jiraConfig?.region_assignees?.[selectedAlert.region] || 
                       jiraConfig?.region_assignees?.["default"]) && 
                      typeof (jiraConfig?.region_assignees?.[selectedAlert.region] || 
                       jiraConfig?.region_assignees?.["default"]) === 'string' &&
                      (jiraConfig?.region_assignees?.[selectedAlert.region] || 
                       jiraConfig?.region_assignees?.["default"]).includes('@') && (
                      <Box component="div" mt={1} fontSize="0.9em" color="text.secondary">
                        This ticket will be assigned to the account associated with this email address.
                      </Box>
                    )}
                  </span>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTicketDialog(false)}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={handleSubmitTicket}
            startIcon={<AssignmentIcon />}
          >
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Jira Integration Info Dialog */}
      <Dialog
        open={showJiraInfo}
        onClose={() => setShowJiraInfo(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Jira Integration
          <IconButton
            onClick={() => setShowJiraInfo(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>Status:</strong> {isJiraEnabled() ? (
              <Chip label="Enabled" color="success" size="small" sx={{ ml: 1 }} />
            ) : (
              <Chip label="Disabled" color="error" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
          
          {isJiraEnabled() ? (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>Jira URL:</strong> {jiraConfig?.url || jiraConfig?.jira_url || "Not configured"}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Project Key:</strong> {jiraConfig?.project_key || "Not configured"}
              </Typography>
              
              <Typography variant="h6" mt={2} mb={1}>
                Region Assignments
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                {jiraConfig?.regions && Array.isArray(jiraConfig.regions) && jiraConfig.regions.map(region => {
                  const assigneeInfo = jiraConfig?.region_assignees?.[region] || 'Not assigned';
                  const isEmail = typeof assigneeInfo === 'string' && assigneeInfo.includes('@');
                  // Handle object or string assigneeInfo properly
                  const displayValue = typeof assigneeInfo === 'object' 
                    ? (assigneeInfo.displayName || assigneeInfo.accountId || 'Unknown') 
                    : assigneeInfo;
                  
                  return (
                    <Tooltip 
                      key={region}
                      title={isEmail ? assigneeInfo : 'Account ID'}
                    >
                      <Chip 
                        label={`${region}: ${displayValue}`}
                        size="medium"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    </Tooltip>
                  );
                })}
                {jiraConfig?.region_assignees?.default && (
                  <Tooltip title={typeof jiraConfig?.region_assignees?.default === 'string' && jiraConfig?.region_assignees?.default.includes('@') ? jiraConfig?.region_assignees.default : 'Account ID'}>
                    <Chip 
                      label={`Default: ${typeof jiraConfig?.region_assignees?.default === 'object' ? 
                        (jiraConfig?.region_assignees?.default.displayName || jiraConfig?.region_assignees?.default.accountId || 'Unknown') 
                        : jiraConfig?.region_assignees?.default || 'Not assigned'}`}
                      size="medium"
                      color="secondary"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Tooltip>
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary" mt={2}>
                Alerts can be automatically assigned to regional administrators based on the region where the alert was triggered.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" mt={2}>
              Jira integration is currently disabled in the system configuration. 
              Tickets can still be created for testing purposes. Please contact your administrator to fully enable Jira integration.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowJiraInfo(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Ticket History Dialog */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Jira Ticket History
          <IconButton
            onClick={() => setShowHistoryDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <LoadingOverlay loading={loading.alertTickets} message="Loading ticket history...">
            {alertTickets && alertTickets.length > 0 ? (
              <Box>
                <Grid container spacing={2}>
                  {alertTickets.map((ticket, index) => (
                    <Grid item xs={12} key={`ticket-${index}`}>
                      <Card 
                        sx={{ 
                          p: 2,
                          borderLeft: `4px solid ${getAlertColor(ticket.type)}`
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6">
                            {ticket.ticket_key}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<LinkIcon />}
                            onClick={() => window.open(ticket.ticket_url, '_blank')}
                          >
                            View
                          </Button>
                        </Stack>
                        
                        <Typography variant="body1" mt={1}>
                          {ticket.type.toUpperCase()} Alert on {ticket.hostname}
                        </Typography>
                        
                        <Box mt={1}>
                          <Chip 
                            label={`Region: ${ticket.region}`}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip 
                            label={`Value: ${ticket.value?.toFixed(1) || 0}%`}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip 
                            label={formatDate(ticket.created_at)}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          {ticket.assignee && (
                            <Chip 
                              label={`Assignee: ${typeof ticket.assignee === 'object' 
                                ? (ticket.assignee.displayName || ticket.assignee.accountId || 'Unknown') 
                                : ticket.assignee}`}
                              size="small"
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: 200
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No ticket history found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create tickets for alerts to see them listed here
                </Typography>
              </Box>
            )}
          </LoadingOverlay>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={6000} 
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsPage;